import { useEffect, useState } from 'react';
import { ReqEnum } from 'const/requestStatus';
import { fetchParcelByOrderID } from 'fetch/parcelFetch';
import { settings } from 'settings';

import Fetch from 'utils/fetch';

import { flatten } from 'utils/array';

import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  Instruction,
} from 'types/bundleTypes';
import { OrderParcels, Region } from 'types/orderTypes';
import { plainToClass } from 'class-transformer';
import * as Sentry from '@sentry/react';
import { Severity } from '@sentry/react';

export interface FetchBundleParcelsResult {
  [bundleId: string]: OrderParcels[];
}

export function useFetchBundlesWithStatuses(
  currentPage = 1,
  statusList: BundleStatus[] = [
    BundleStatus.Published,
    BundleStatus.Proposed,
    BundleStatus.Accepted,
    BundleStatus.Completed,
  ],
  region: Region,
  errorHandler: (error: Error) => void = undefined
): [FetchBundleParcelsResult, BundlesPageJson, ReqEnum] {
  const [fetchStatus, setFetchStatus] = useState<ReqEnum>(ReqEnum.INIT);
  const [bundles, setBundles] = useState<BundlesPageJson>(null);
  const [parcels, setParcels] = useState<FetchBundleParcelsResult>(null);

  useEffect(() => {
    setFetchStatus(ReqEnum.PENDING);
    fetchBundlesWithStatuses(currentPage, statusList, region)
      .then(([bundles, parcels]) => {
        setBundles(bundles as BundlesPageJson);
        setParcels(parcels as FetchBundleParcelsResult);
        setFetchStatus(ReqEnum.SUCCESS);
      })
      .catch(reason => {
        if (reason instanceof TypeError) {
          Sentry.captureException(reason, {
            level: Severity.Warning,
          });
          setFetchStatus(ReqEnum.ERROR);
        } else if (errorHandler) {
          errorHandler(reason);
        } else {
          throw reason;
        }
      });
  }, [currentPage, statusList, errorHandler, region]);

  return [parcels, bundles, fetchStatus];
}

async function fetchBundlesWithStatuses(
  currentPage: number,
  statusList: BundleStatus[],
  region: Region
): Promise<[BundlesPageJson, { [bundleId: string]: OrderParcels[] }]> {
  const bundlesJsonRaw = await Fetch.get({
    url: `${settings.newApiUrl}/bundle?status=${statusList.join(
      ','
    )}&page=${currentPage}&region=${region}`,
  });

  if (!bundlesJsonRaw.ok) {
    throw Error(bundlesJsonRaw.statusText);
  }

  const bundlesPage = plainToClass(
    BundlesPageJson,
    await bundlesJsonRaw.json()
  );

  const parcels: { [bundleId: string]: OrderParcels[] } = await fetchParcelData(
    bundlesPage.items
  );

  return [bundlesPage, parcels];
}

async function fetchParcelData(
  bundles: BundleJson[]
): Promise<FetchBundleParcelsResult> {
  const ordersBundle = {};
  // batch parcel requests for all bundles
  const instructions: Instruction[] = flatten(
    bundles.map(bundle => {
      bundle.instructions.forEach(
        instr => (ordersBundle[instr.orderId] = bundle.id)
      );
      return bundle.instructions;
    })
  );
  const pickupInstructionsIds = instructions
    .filter(item => item.type === 'PICKUP')
    .map(item => item.orderId);

  const parcels = await fetchParcelByOrderID(pickupInstructionsIds);

  // divide parcel results by bundle
  const parcelsTable: FetchBundleParcelsResult = {};
  parcels.forEach(parcels => {
    const bundleId = ordersBundle[parcels.orderId];
    if (!parcelsTable[bundleId]) {
      parcelsTable[bundleId] = [];
    }
    parcelsTable[bundleId].push(parcels);
  });

  return parcelsTable;
}
